import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true, 
  redirectTo = '/login' 
}) => {
  const { isAuthenticated, isInitialized } = useAuth();
  const location = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    // If route requires authentication but user is not authenticated
    if (requireAuth && !isAuthenticated) {
      setRedirectPath(redirectTo);
      setShouldRedirect(true);
      return;
    }

    // If route doesn't require authentication but user is authenticated
    if (!requireAuth && isAuthenticated) {
      setRedirectPath('/home');
      setShouldRedirect(true);
      return;
    }

    setShouldRedirect(false);
    setRedirectPath(null);
  }, [isAuthenticated, isInitialized, requireAuth, redirectTo, location.pathname]);


  // Show loading screen until auth is initialized
  if (!isInitialized) {
    return (
      <div className="h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
          <p className="text-white text-lg">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Handle redirect
  if (shouldRedirect && redirectPath) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Public route component for login/register pages
export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ProtectedRoute requireAuth={false}>{children}</ProtectedRoute>;
};

// Protected route component for authenticated pages
export const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ProtectedRoute requireAuth={true}>{children}</ProtectedRoute>;
};

